// Terminação garantida pra `await` que pode NÃO ASSENTAR.
//
// A regressão que originou este módulo (catálogo vazio no `.ipk` de develop):
// o gate do `useCatalogRefresh` passou a depender de `phase === 'done'` do
// `boot-snapshot`, e QUALQUER `await` pendurado no caminho de boot deixava a
// fase presa num estado transitório — pra sempre, porque o effect que dispara o
// boot tem deps `[isAuthed]` e não re-roda. Sem catálogo, sem tick de 5s, sem
// poll de 30min, sem refresh ao voltar do standby.
//
// O invariante que este módulo existe pra sustentar:
//   **nenhum gate de carga de dados pode depender de um estado sem terminação
//   garantida.**
//
// Um `Promise` de rede ou de IndexedDB não tem terminação garantida por
// construção: `fetch` nativo não tem timeout (o próprio `api.ts` já documenta
// isso e resolve com AbortController no `request()`), e uma transação de IDB
// pode ficar pendurada indefinidamente no evento `blocked` — que é o caso REAL
// em Tizen 2017 com quota apertada, onde outra aba/versão segura o upgrade.
//
// Todo helper aqui é feito em DUAS camadas:
//   1. um `race` contra um timer, que LIBERA o `await` — sempre, e é ela que dá
//      a garantia;
//   2. um `abort` por CAPABILITY (`typeof AbortController === 'function'`), que
//      além disso solta o socket.
//
// A camada 1 não é redundante com a 2, por DOIS motivos independentes.
//
// (a) ESCOPO: um `signal` só cancela `fetch`. Ele não faz nada por uma transação
//     de IndexedDB pendurada no `blocked` nem por um Worker que não responde —
//     dois dos casos reais que este módulo existe pra cobrir. Abortar resolve UM
//     dos awaits; o race resolve todos.
//
// (b) MOTOR, numa FAIXA do fleet (medido, não deduzido). A global
//     `AbortController` existir NÃO significa que abortar funcione. O build de TV
//     injeta `abortcontroller-polyfill/dist/abortcontroller-polyfill-only`
//     (`vite.config.ts`, `additionalLegacyPolyfills`), e a variante `-only`
//     instala a CLASSE mas nunca torna o `fetch` abortável — o próprio vendor
//     documenta isso em `dist/abortcontroller-polyfill-only.js:461`. Onde o
//     `fetch` também é polyfillado (Chr38, sem `fetch` nativo) o par se completa
//     e o abort funciona. Mas o `whatwg-fetch` só se instala sob
//     `if (!g.fetch)`, e `fetch` é nativo desde o Chr42 — então de Chr42 a Chr63
//     o `fetch` é o NATIVO, que ignora `signal` (só o entende no Chr66+) e ignora
//     `cache` (só no Chr64+), em silêncio, porque membro desconhecido de
//     dictionary WebIDL é descartado sem erro.
//
//     Consequência contraintuitiva e medida em engine real: o PISO (Chr38) é o
//     único seguro; quem quebra é a FAIXA DO MEIO — webOS 4.x (Chr53) e Tizen
//     2017-2018 (Chr47+). Nessa faixa, `setTimeout(() => ctrl.abort(), ms)` é
//     inerte: o `await` não assenta NUNCA por conta do prazo. Só o race assenta.
//     Ver docs/changelog/tv-deadline-failover-chr53.md.
//
// O guard por capability fica assim mesmo (mesmo padrão de
// `telemetry-transport.ts`): custa nada e não depende da ordem de injeção de
// polyfill continuar correta pra sempre.
//
// Todo helper LIMPA o próprio timer no caminho feliz. Timer pendurado é
// alocação que ninguém libera, e a regra da casa em long-session é cleanup
// ativo — não "o GC resolve".

/** `name` do erro de prazo. Não usamos `class X extends Error`: no build legacy
 *  o Babel transpila a herança de builtin e `instanceof` para de valer no piso
 *  antigo. Comparar `name` funciona em qualquer motor. */
const DEADLINE_ERROR_NAME = 'DeadlineError'

export function deadlineError(label: string, ms: number): Error {
  const err = new Error(`[deadline] "${label}" não assentou em ${ms}ms`)
  err.name = DEADLINE_ERROR_NAME
  return err
}

/**
 * `true` se o erro veio de um prazo estourado deste módulo.
 *
 * Existe pra que quem consome o race NÃO precise repetir a string
 * `'DeadlineError'` — o mesmo motivo de `deadlineError` comparar `name` em vez de
 * `instanceof`: no build legacy o Babel transpila herança de builtin e
 * `instanceof Error` para de valer no piso antigo.
 */
export function isDeadlineError(err: unknown): boolean {
  return !!err && (err as Error).name === DEADLINE_ERROR_NAME
}

/**
 * Resolve com `fallback` se `p` não assentar em `ms`. NUNCA rejeita por prazo
 * (a rejeição do próprio `p` continua propagando normalmente).
 *
 * Use quando existe uma resposta honesta pro "não sei": ler o meta do IDB e não
 * obter resposta é indistinguível, pra quem chama, de "não tem meta" — e a
 * decisão que segue (baixar o snapshot) é a mesma. Não use quando o fallback
 * seria uma MENTIRA (ex.: dizer que gravou).
 */
export function orFallbackAfter<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null
  const guard = new Promise<T>((resolve) => {
    timer = setTimeout(() => {
      timer = null
      resolve(fallback)
    }, ms)
  })
  const clear = (): void => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }
  // `.then(ok, err)` e não `.finally`: `Promise.prototype.finally` é ES2018
  // (Chr63+) e o piso aqui é Chr38.
  return Promise.race([p, guard]).then(
    (v) => {
      clear()
      return v
    },
    (e: unknown) => {
      clear()
      throw e
    },
  )
}

/**
 * Rejeita com `deadlineError` se `p` não assentar em `ms`.
 *
 * Use quando não existe fallback honesto e o caller precisa SABER que falhou
 * (o erro vira um desfecho terminal — que é justamente o que faltava).
 */
export function rejectAfter<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null
  const guard = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      timer = null
      reject(deadlineError(label, ms))
    }, ms)
  })
  const clear = (): void => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }
  return Promise.race([p, guard]).then(
    (v) => {
      clear()
      return v
    },
    (e: unknown) => {
      clear()
      throw e
    },
  )
}

export interface NetDeadline {
  /** `undefined` só se a global não existir (no build de TV ela existe: o
   *  polyfill entra antes do entry). O `race` contra `expired` libera o `await`
   *  de qualquer forma; sem `signal`, só o socket é que fica pra trás. */
  signal?: AbortSignal
  /** Rejeita quando o prazo estoura; nunca resolve. Feita pra entrar em
   *  `Promise.race` quantas vezes for preciso (um `read()` por chunk). Já nasce
   *  com um handler interno, então estourar o prazo depois do caminho feliz não
   *  vira `unhandledrejection`. */
  expired: Promise<never>
  /** Re-arma o prazo. É o que transforma "prazo TOTAL" em "prazo entre dois
   *  bytes": um download legítimo de 5-15MB em vlow não pode morrer por ser
   *  LENTO, só por PARAR. */
  bump: (ms?: number) => void
  /** Desarma. Obrigatório no caminho feliz. */
  clear: () => void
}

/**
 * Prazo de rede com abort por capability + re-arme.
 *
 * O timer não segura o processo em Node (`unref` quando existe) — irrelevante
 * no browser, mas evita que a suíte de teste fique pendurada por um prazo
 * armado que ninguém desarmou.
 */
export function makeNetDeadline(ms: number, label: string): NetDeadline {
  let timer: ReturnType<typeof setTimeout> | null = null
  let fired = false
  let cleared = false
  let ctrl: AbortController | null = null

  let reject: (err: Error) => void = () => {}
  const expired = new Promise<never>((_resolve, rej) => {
    reject = rej
  })
  // Handler interno: mantém a rejeição "tratada" mesmo que ninguém esteja
  // correndo contra ela na hora em que estourar.
  expired.then(null, () => {})

  const fire = (waited: number): void => {
    if (fired || cleared) return
    fired = true
    timer = null
    if (ctrl) {
      try {
        ctrl.abort()
      } catch {
        // Motor antigo com AbortController parcial: abortar é o extra, não a
        // garantia. O `reject` abaixo é que libera o await.
      }
    }
    reject(deadlineError(label, waited))
  }

  const arm = (waitMs: number): void => {
    if (cleared || fired) return
    if (timer !== null) clearTimeout(timer)
    timer = setTimeout(() => fire(waitMs), waitMs)
    const t = timer as unknown as { unref?: () => void }
    if (typeof t.unref === 'function') t.unref()
  }

  // Capability, não marca. No build de TV o polyfill garante a global; o guard
  // é defesa em profundidade (e cobre teste/SSR onde ela pode não existir).
  if (typeof AbortController === 'function') ctrl = new AbortController()

  arm(ms)

  const out: NetDeadline = {
    expired,
    bump: (nextMs?: number) => arm(nextMs ?? ms),
    clear: () => {
      cleared = true
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    },
  }
  // `exactOptionalPropertyTypes`: atribuição CONDICIONAL, nunca
  // `{ signal: undefined }`.
  if (ctrl) out.signal = ctrl.signal
  return out
}
